import React, { useRef, useState } from 'react';
import {
  BoltIcon,
  LinkIcon,
  ListBulletIcon,
  CodeBracketIcon,
  ChatBubbleLeftRightIcon,
  Bars3BottomLeftIcon,
  DocumentTextIcon
} from '@heroicons/react/24/outline';

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  rows?: number;
  className?: string;
  required?: boolean;
  id?: string;
}

const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value,
  onChange,
  placeholder = 'Írja be a szöveget...',
  rows = 8,
  className = '',
  required = false,
  id
}) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [showPreview, setShowPreview] = useState(false);

  // Szöveg beszúrása a kurzor pozíciójára
  const insertText = (before: string, after: string = '') => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = value.substring(start, end);
    const newText = value.substring(0, start) + before + selectedText + after + value.substring(end);
    
    onChange(newText);
    
    // Kurzor pozíció visszaállítása
    setTimeout(() => {
      textarea.focus();
      const newCursorPos = start + before.length + selectedText.length;
      textarea.setSelectionRange(newCursorPos, newCursorPos);
    }, 0);
  };

  // Formázó gombok
  const formatButtons = [
    {
      icon: BoltIcon,
      title: 'Félkövér (Ctrl+B)',
      action: () => insertText('**', '**'),
      shortcut: 'Ctrl+B'
    },
    {
      icon: Bars3BottomLeftIcon,
      title: 'Dőlt (Ctrl+I)',
      action: () => insertText('*', '*'),
      shortcut: 'Ctrl+I'
    },
    {
      icon: LinkIcon,
      title: 'Link beszúrása',
      action: () => {
        const url = prompt('Adja meg a link címét:');
        if (url) {
          insertText('[', `](${url})`);
        }
      }
    },
    {
      icon: ListBulletIcon,
      title: 'Felsorolás',
      action: () => insertText('\n- ', '')
    },
    {
      icon: DocumentTextIcon,
      title: 'Számozott lista',
      action: () => insertText('\n1. ', '')
    },
    {
      icon: CodeBracketIcon,
      title: 'Kód',
      action: () => insertText('`', '`')
    },
    {
      icon: ChatBubbleLeftRightIcon,
      title: 'Idézet',
      action: () => insertText('\n> ', '')
    }
  ];

  // Billentyű kombinációk kezelése
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.ctrlKey || e.metaKey) {
      switch (e.key.toLowerCase()) {
        case 'b':
          e.preventDefault();
          insertText('**', '**');
          break;
        case 'i':
          e.preventDefault();
          insertText('*', '*');
          break;
        case 'k':
          e.preventDefault();
          const url = prompt('Adja meg a link címét:');
          if (url) {
            insertText('[', `](${url})`);
          }
          break;
      }
    }
  };

  // Markdown előnézet (egyszerű verzió)
  const renderPreview = (text: string) => {
    let html = text
      // Félkövér
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      // Dőlt
      .replace(/\*(.*?)\*/g, '<em>$1</em>')
      // Link
      .replace(/\[(.*?)\]\((.*?)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer" class="text-blue-600 dark:text-blue-400 hover:underline">$1</a>')
      // Kód
      .replace(/`(.*?)`/g, '<code class="bg-gray-100 dark:bg-gray-700 px-1 py-0.5 rounded text-sm">$1</code>')
      // Sortörések
      .replace(/\n/g, '<br />');

    return html;
  };

  return (
    <div className={`border border-gray-300 dark:border-gray-600 rounded-lg overflow-hidden ${className}`}>
      {/* Eszköztár */}
      <div className="bg-gray-50 dark:bg-gray-700 border-b border-gray-300 dark:border-gray-600 p-2 flex items-center justify-between">
        <div className="flex items-center space-x-1">
          {formatButtons.map((button, index) => (
            <button
              key={index}
              type="button"
              onClick={button.action}
              title={button.title}
              className="p-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-200 dark:hover:bg-gray-600 rounded transition-colors"
            >
              <button.icon className="h-4 w-4" />
            </button>
          ))}
        </div>
        
        {/* Előnézet kapcsoló */}
        <button
          type="button"
          onClick={() => setShowPreview(!showPreview)}
          className="px-3 py-1 text-xs font-medium text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-200 dark:hover:bg-gray-600 rounded transition-colors"
        >
          {showPreview ? 'Szerkesztés' : 'Előnézet'}
        </button>
      </div>

      {/* Szövegmező / Előnézet */}
      {showPreview ? (
        <div 
          className="p-4 min-h-[200px] bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 prose dark:prose-invert max-w-none"
          dangerouslySetInnerHTML={{ __html: renderPreview(value) }}
        />
      ) : (
        <textarea
          ref={textareaRef}
          id={id}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={handleKeyDown}
          rows={rows}
          required={required}
          placeholder={placeholder}
          className="w-full bg-white dark:bg-gray-800 px-4 py-3 text-sm text-gray-700 dark:text-gray-200 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none resize-y"
        />
      )}

      {/* Súgó szöveg */}
      <div className="bg-gray-50 dark:bg-gray-700 border-t border-gray-300 dark:border-gray-600 px-3 py-2">
        <p className="text-xs text-gray-500 dark:text-gray-400">
          <strong>Markdown formázás:</strong> **félkövér**, *dőlt*, [link](url), `kód`, {'>'} idézet
        </p>
      </div>
    </div>
  );
};

export default RichTextEditor;
